import React, { useState, useMemo } from 'react';
import SelectCheckbox from '../../../../shared/select-checkbox';
import DateRangePopover from './DateRange';
import Input from '../../../../shared/input';
import { useTheme } from '../../../../utils/theme-context';

export const STATUS_RAG = [
    { label: 'Completed', value: 'completed' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Active', value: 'active' },
    { label: 'Cancelled', value: 'cancelled' },
]

interface ITripDetailsFilter {
    selectedRag?: any;
    journeys: any;
    searchText: any;
    trainManagers: any;
    selectedDetailsJourneys?: any;
    selectedDetailsManager?: any;
    startDateRange?: any;
    endDateRange?: any;
    onChangeStartTripsDate: (value: any) => void
    onChangeEndTripsDate: (value: any) => void
    onChangeSelectedFilter: (value: any, type?: string) => void;
    onChangeSearchValue: (value: any) => void;
    onResetFilters: () => void;
}

const formatDateShort = (d: Date) =>
    `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

const TripDetailsFilters = ({
    selectedRag,
    journeys,
    trainManagers,
    selectedDetailsJourneys,
    selectedDetailsManager,
    startDateRange,
    endDateRange,
    searchText,
    onChangeStartTripsDate,
    onChangeEndTripsDate,
    onChangeSelectedFilter,
    onChangeSearchValue,
    onResetFilters
}: ITripDetailsFilter) => {
    const { theme } = useTheme();
    const [filtersOpen, setFiltersOpen] = useState(true);

    // Active filters for pills
    const activeFilters = useMemo(() => {
        const pills: { label: string; key: string }[] = [];
        if (selectedRag && selectedRag?.value !== 'all') {
            pills.push({ label: `Status: ${selectedRag?.label || selectedRag?.value}`, key: 'rag' });
        }
        if (selectedDetailsManager && selectedDetailsManager?.value !== 'all') {
            pills.push({ label: `Train Manager: ${selectedDetailsManager?.label || selectedDetailsManager?.value}`, key: 'trainManagers' });
        }
        if (selectedDetailsJourneys && selectedDetailsJourneys?.value !== 'all') {
            pills.push({ label: `Itinerary: ${selectedDetailsJourneys?.label || selectedDetailsJourneys?.value}`, key: 'journeys' });
        }
        if (startDateRange && startDateRange[0] && startDateRange[1]) {
            pills.push({
                label: `Start: ${formatDateShort(startDateRange[0])} - ${formatDateShort(startDateRange[1])}`,
                key: 'startDate',
            });
        }
        if (endDateRange && endDateRange[0] && endDateRange[1]) {
            pills.push({
                label: `End: ${formatDateShort(endDateRange[0])} - ${formatDateShort(endDateRange[1])}`,
                key: 'endDate',
            });
        }
        if (searchText) {
            pills.push({ label: `Search: ${searchText}`, key: 'search' });
        }
        return pills;
    }, [selectedRag, selectedDetailsManager, selectedDetailsJourneys, startDateRange, endDateRange, searchText]);

    const handleRemoveFilter = (key: string) => {
        if (key === 'rag') onChangeSelectedFilter(undefined, 'rag');
        if (key === 'trainManagers') onChangeSelectedFilter(undefined, 'trainManagers');
        if (key === 'journeys') onChangeSelectedFilter(undefined, 'journeys');
        if (key === 'startDate') onChangeStartTripsDate(undefined);
        if (key === 'endDate') onChangeEndTripsDate(undefined);
        if (key === 'search') onChangeSearchValue({ value: '', name: 'search' });
    };

    return (
        <div className='my-2 rounded-xl border border-gray-200 bg-white shadow-sm dark:bg-[#111317] dark:border-[#252B37]'>
            {/* ── Header ─────────────────────────────────────────── */}
            <div
                className='flex items-center justify-between px-6 py-3.5 rounded-tl-xl rounded-tr-xl'
                style={{ backgroundColor: theme === 'dark' ? '#252B37' : '#FAFAFA', borderBottom: theme === 'dark' ? '1px solid #ffffff1a' : '1px solid #F5F5F5' }}
            >
                {/* Left: Filter icon + Apply Filters (clickable) */}
                <button
                    onClick={() => setFiltersOpen((v) => !v)}
                    className='flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white hover:text-gray-700 transition'
                >
                    <img src="/svg/filter_icon.svg" className="w-[15px] h-[15px] flex-shrink-0 dark:contrast-0" alt="filter" />
                    {/* <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' />
                    </svg> */}
                    Apply Filters
                </button>

                {/* Right: Collapse toggle */}
                <button onClick={() => setFiltersOpen((v) => !v)} className='ml-1 text-gray-500 hover:text-gray-700 transition'>
                    {filtersOpen ? (
                        <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            {/* Open: top arrow points down, bottom arrow points up — pointing at each other */}
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 4l5 4 5-4' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 20l5-4 5 4' />
                        </svg>
                    ) : (
                        <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            {/* Closed: top arrow points up, bottom arrow points down — pointing away */}
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 10l5-4 5 4' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 14l5 4 5-4' />
                        </svg>
                    )}
                </button>
            </div>

            {/* ── Collapsible Body ───────────────────────────────── */}
            <div
                className='transition-all duration-500 ease-in-out'
                style={{
                    maxHeight: filtersOpen ? '600px' : '0px',
                    opacity: filtersOpen ? 1 : 0,
                    overflow: filtersOpen ? 'visible' : 'hidden',
                }}
            >
                <div className='px-6 py-5 space-y-5'>
                    {/* Row 1: Status + Train Manager + Itinerary */}
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <SelectCheckbox
                            name='Status'
                            placeholder='All Statuses'
                            options={STATUS_RAG}
                            selectedOption={selectedRag}
                            uniqueName='rag'
                            onChangeSelectedOption={onChangeSelectedFilter}
                            containerStyle='mb-0 w-full'
                        />
                        <SelectCheckbox
                            name='Train Manager'
                            placeholder='All Train Managers'
                            options={trainManagers}
                            selectedOption={selectedDetailsManager}
                            uniqueName='trainManagers'
                            onChangeSelectedOption={onChangeSelectedFilter}
                            containerStyle='mb-0 w-full'
                        />
                        <SelectCheckbox
                            name='Itinerary'
                            placeholder='All Itineraries'
                            options={journeys}
                            selectedOption={selectedDetailsJourneys}
                            uniqueName='journeys'
                            onChangeSelectedOption={onChangeSelectedFilter}
                            containerStyle='mb-0 w-full'
                        />
                    </div>

                    {/* Row 2: Start Date Range + End Date Range + Search */}
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <DateRangePopover
                            label='Start Date Range'
                            value={startDateRange}
                            showTime={true}
                            onChange={onChangeStartTripsDate}
                            containerStyle=''
                            labelStyle='text-gray-500 dark:!text-gray-300 !font-normal !font-inter !text-[#0A0D12] !text-[14px]'
                        />
                        <DateRangePopover
                            label='End Date Range'
                            value={endDateRange}
                            showTime={true}
                            onChange={onChangeEndTripsDate}
                            containerStyle=''
                            labelStyle='text-gray-500 dark:!text-gray-300 !font-normal !font-inter !text-[#0A0D12] !text-[14px]'
                        />
                        <div className='flex items-end'>
                            <Input
                                placeholder={'Search'}
                                containerStyle='mb-0 w-full relative dark:bg-bgColor-darkMode'
                                inputStyle='mb-0 border border-gray-300 rounded-lg focus:outline-none dark:bg-black dark:text-primary-darkMode'
                                icon={theme === 'dark' ? '/assets/navigation-icons/sidebar/search-icon-white.svg' : '/assets/shared/search.svg'}
                                showValue={true}
                                inputValue={searchText}
                                inputUniqueName={'search'}
                                onChangeInput={onChangeSearchValue}
                                hideInputSpace={true}
                            />
                        </div>
                    </div>

                    {/* Row 3: Active filter pills + actions */}
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <div className='flex flex-wrap items-center gap-2'>
                            {activeFilters.map((f) => (
                                <span
                                    key={f.key}
                                    className='inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-200'
                                >
                                    {f.label}
                                    <button
                                        onClick={() => handleRemoveFilter(f.key)}
                                        className='text-blue-500 hover:text-blue-700'
                                    >
                                        <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                        </svg>
                                    </button>
                                </span>
                            ))}
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                onClick={onResetFilters}
                                className='flex items-center gap-2 rounded-[10px] h-[35px] bg-[#F1F5F9] border-gray-300 px-5 py-2.5 text-sm text-[#1E293B] font-bold !text-[12px] w-[115px] font-plus-jakarta hover:bg-gray-100 transition'
                            >
                                <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                                </svg>
                                Clear All
                            </button>
                            <button
                                onClick={() => {/* filters are applied live via onChange already */ }}
                                className='  bg-[#1E293B] h-[35px] rounded-[10px] text-white text-sm font-medium hover:bg-gray-800 transition !text-[12px] w-[137px] font-plus-jakarta'
                            >
                                Show
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default TripDetailsFilters
